import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Send, PartyPopper, CalendarDays } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, SectionHeading, Sparkles } from "../components/ui-bits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_KEYS = ["bowling", "discobowling", "jenever", "arrangement", "kinderfeestje", "other"];
const ARR_KEYS = ["vlaams", "italiaans", "koud-buffet", "steengrill"];
const FORMULA_KEYS = ["frietjes", "spaghetti", "broodje", "pannenkoeken", "frikadellen", "pizza"];

const empty = { name: "", email: "", phone: "", people: 2, date: "", time: "", type: "bowling", arrangement: "", formula: "", message: "" };

export default function Reserveer() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isArrangement = form.type === "arrangement";
  const isKids = form.type === "kinderfeestje";
  const isDisco = form.type === "discobowling" || form.type === "jenever";

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      setStatus("emailerror");
      return;
    }
    const mins = Number((form.time.split(":")[1] || "0"));
    if (mins !== 0 && mins !== 30) {
      setStatus("timeerror");
      return;
    }
    setStatus("sending");
    const payload = {
      ...form,
      email: form.email.trim(),
      phone: form.phone.trim(),
      name: form.name.trim(),
      people: Number(form.people),
      arrangement: isArrangement && form.arrangement ? form.arrangement : null,
      formula: isKids && form.formula ? form.formula : null,
      message: form.message || null,
      lang,
      submission_id: submissionId,
    };
    const postReservation = () => axios.post(`${API}/reservations`, payload, { timeout: 45000 });
    try {
      try {
        await postReservation();
      } catch (firstErr) {
        if (firstErr.response) throw firstErr;
        await postReservation();
      }
      setStatus("success");
    } catch (err) {
      setStatus(err.response ? "error" : "neterror");
    }
  };

  return (
    <main className="relative pt-32 pb-24 overflow-hidden">
      <Sparkles count={12} />
      <div className="max-w-6xl mx-auto px-5">
        <SectionHeading eyebrow="Helios" title={t("reserve.title")} sub={t("reserve.sub")} />
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
        {status === "success" ? (
          <Reveal>
            <div className="glass rounded-3xl p-12 text-center" data-testid="reservation-success">
              <PartyPopper className="mx-auto text-[#F0C971] mb-5" size={48} />
              <h3 className="font-display text-3xl uppercase neon">{t("reserve.successTitle")}</h3>
              <p className="mt-4 text-[#9db8d6]">{t("reserve.successText")}</p>
              <button
                data-testid="reservation-again-button"
                onClick={() => { setForm(empty); setSubmissionId(crypto.randomUUID()); setStatus("idle"); }}
                className="btn-ghost mt-8 font-display tracking-[0.2em] uppercase px-8 py-3 rounded-full"
              >
                {t("reserve.again")}
              </button>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={submit} className="glass rounded-3xl p-8 sm:p-10 space-y-5" data-testid="reservation-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.name")} *</label>
                  <input required data-testid="reservation-name-input" className="field" value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.email")} *</label>
                  <input required type="email" data-testid="reservation-email-input" className="field" value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.phone")} *</label>
                  <input required type="tel" data-testid="reservation-phone-input" className="field" value={form.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.people")} *</label>
                  <input required type="number" min="1" max="100" data-testid="reservation-people-input" className="field" value={form.people} onChange={set("people")} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.date")} *</label>
                  <input required type="date" data-testid="reservation-date-input" className="field" value={form.date} onChange={set("date")} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.time")} *</label>
                  <input required type="time" step="1800" data-testid="reservation-time-input" className="field" value={form.time} onChange={set("time")} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.type")} *</label>
                <select data-testid="reservation-type-select" className="field" value={form.type} onChange={set("type")}>
                  {TYPE_KEYS.map((k) => (
                    <option key={k} value={k}>{t(`reserve.types.${k}`)}</option>
                  ))}
                </select>
              </div>

              {isKids && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.formula")}</label>
                  <select data-testid="reservation-formula-select" className="field" value={form.formula} onChange={set("formula")}>
                    <option value="">—</option>
                    {FORMULA_KEYS.map((k) => (
                      <option key={k} value={k}>{t(`reserve.formulaOptions.${k}`)}</option>
                    ))}
                  </select>
                </div>
              )}

              {isDisco && (
                <div className="rounded-xl border border-[#F0C971]/50 bg-[#F0C971]/10 p-4" data-testid="reservation-disco-warning">
                  <p className="flex gap-2 items-start text-sm text-[#E2F1FF] leading-relaxed">
                    <AlertTriangle size={16} className="text-[#F0C971] shrink-0 mt-0.5" />
                    <span>
                      {t("reserve.discoWarn1")}
                      <Link to="/jenever-discobowling" data-testid="reservation-disco-link" className="text-[#00F0FF] font-bold underline underline-offset-4 hover:text-white transition-colors">
                        {t("reserve.discoWarnLink")}
                      </Link>
                      {t("reserve.discoWarn2")}
                    </span>
                  </p>
                </div>
              )}

              {isArrangement && (
                <>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.arrangement")} *</label>
                    <select required data-testid="reservation-arrangement-select" className="field" value={form.arrangement} onChange={set("arrangement")}>
                      <option value="">—</option>
                      {ARR_KEYS.map((k) => (
                        <option key={k} value={k}>{t(`reserve.arrOptions.${k}`)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="rounded-xl border border-[#F0C971]/50 bg-[#F0C971]/10 p-4 space-y-2" data-testid="reservation-deposit-warning">
                    <p className="flex gap-2 items-center text-sm font-bold text-[#F0C971] uppercase tracking-wider">
                      <AlertTriangle size={16} /> {t("reserve.arrWarning")}
                    </p>
                    <p className="text-xs text-[#E2F1FF] leading-relaxed">{t("reserve.deposit")}</p>
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("reserve.message")}</label>
                <textarea rows="4" data-testid="reservation-message-input" className="field" value={form.message} onChange={set("message")} />
              </div>

              {status === "error" && (
                <p className="text-sm text-red-400 flex gap-2 items-center" data-testid="reservation-error">
                  <AlertTriangle size={15} /> {t("reserve.error")}
                </p>
              )}

              {status === "timeerror" && (
                <p className="text-sm text-[#F0C971] flex gap-2 items-center" data-testid="reservation-time-error">
                  <AlertTriangle size={15} /> {t("reserve.timeError")}
                </p>
              )}

              {status === "neterror" && (
                <p className="text-sm text-[#F0C971] flex gap-2 items-center" data-testid="reservation-net-error">
                  <AlertTriangle size={15} /> {t("reserve.netError")}
                </p>
              )}

              {status === "emailerror" && (
                <p className="text-sm text-[#F0C971] flex gap-2 items-center" data-testid="reservation-email-error">
                  <AlertTriangle size={15} /> {t("reserve.emailError")}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="reservation-submit-button"
                className="btn-neon w-full font-display text-lg tracking-[0.25em] uppercase px-8 py-4 rounded-full inline-flex items-center justify-center gap-3 disabled:opacity-60"
              >
                {status === "sending" ? t("reserve.sending") : <>{t("reserve.submit")} <Send size={17} /></>}
              </button>
              <p className="text-xs text-[#9db8d6] flex gap-2 items-center justify-center">
                <CheckCircle2 size={13} className="text-[#00F0FF]" /> Gerheide 48, 2490 Balen · +32 (0)14 81 20 93
              </p>
            </form>
          </Reveal>
        )}
          </div>
          <Reveal delay={0.1} className="order-first lg:order-none">
            <aside className="glass rounded-3xl p-6 space-y-4 lg:sticky lg:top-28 mb-8 lg:mb-0" data-testid="friday-theme-news">
              <h3 className="font-display text-lg uppercase tracking-widest neon flex items-center gap-2">
                <CalendarDays size={17} /> {t("reserve.fridayNewsTitle")}
              </h3>
              <p className="text-xs text-[#9db8d6] leading-relaxed">{t("reserve.fridayNewsSub")}</p>
              <a href="https://www.instagram.com/bowlinghelios" target="_blank" rel="noreferrer" data-testid="friday-news-link" className="btn-ghost inline-flex items-center gap-2 text-xs font-display tracking-[0.15em] uppercase px-4 py-2 rounded-full">
                {t("reserve.fridayNewsCta")}
              </a>
            </aside>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
